import { alpha } from '@mui/material/styles'
import type { Theme } from '@mui/material/styles'
import type { ClockMetaResponse } from '../api/showClock'
import {
  formatStationTime,
  resolveEpochMs,
  resolveStationTimeZone,
  stationLocalDateTimeToEpochMs,
} from './time'

export interface ClockItemTypeMeta {
  key: string
  label: string
  colorToken: string
  defaultDurationSeconds: number
}

export const FALLBACK_CLOCK_ITEM_TYPES: Record<string, ClockItemTypeMeta> = {
  song: { key: 'song', label: 'Song', colorToken: 'primaryContainer', defaultDurationSeconds: 210 },
  link: { key: 'link', label: 'Presenter Link', colorToken: 'secondaryContainer', defaultDurationSeconds: 120 },
  news: { key: 'news', label: 'News Bulletin', colorToken: 'tertiaryContainer', defaultDurationSeconds: 180 },
  weather: { key: 'weather', label: 'Weather Update', colorToken: 'tertiaryContainer', defaultDurationSeconds: 90 },
  travel: { key: 'travel', label: 'Travel / Traffic', colorToken: 'tertiaryContainer', defaultDurationSeconds: 120 },
  jingle_id: { key: 'jingle_id', label: 'Jingle / ID', colorToken: 'surfaceContainer', defaultDurationSeconds: 20 },
  commercial_break: {
    key: 'commercial_break',
    label: 'Commercial Break',
    colorToken: 'errorContainer',
    defaultDurationSeconds: 240,
  },
  promo: { key: 'promo', label: 'Promo / Trail', colorToken: 'secondaryContainer', defaultDurationSeconds: 60 },
  custom: { key: 'custom', label: 'Custom Block', colorToken: 'surfaceContainerHigh', defaultDurationSeconds: 60 },
}

export const resolveClockItemTypes = (meta: ClockMetaResponse | null = null): ClockItemTypeMeta[] => {
  const entries = new Map<string, ClockItemTypeMeta>()

  Object.values(FALLBACK_CLOCK_ITEM_TYPES).forEach((item) => {
    entries.set(item.key, { ...item })
  })

  if (meta?.item_types) {
    Object.entries(meta.item_types).forEach(([key, value]) => {
      const fallback = FALLBACK_CLOCK_ITEM_TYPES[key]
      const duration = Number(value?.default_duration_seconds)
      entries.set(key, {
        key,
        label: value?.label ?? fallback?.label ?? key,
        colorToken: value?.color_token ?? fallback?.colorToken ?? 'surfaceContainer',
        defaultDurationSeconds:
          Number.isFinite(duration) && duration > 0 ? Number(duration) : fallback?.defaultDurationSeconds ?? 60,
      })
    })
  }

  return Array.from(entries.values())
}

export const resolveClockItemColorStyles = (
  theme: Theme,
  token: string,
): { backgroundColor: string; color: string } => {
  const primaryBg = alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.3 : 0.12)
  const secondaryBg = alpha(theme.palette.secondary.main, theme.palette.mode === 'dark' ? 0.3 : 0.12)
  const infoBg = alpha(theme.palette.info.main, theme.palette.mode === 'dark' ? 0.3 : 0.15)
  const errorBg = alpha(theme.palette.error.main, theme.palette.mode === 'dark' ? 0.3 : 0.15)
  const surfaceBg = theme.palette.mode === 'dark' ? alpha('#ffffff', 0.12) : alpha(theme.palette.text.primary, 0.06)
  const surfaceHighBg = theme.palette.mode === 'dark' ? alpha('#ffffff', 0.2) : alpha(theme.palette.text.primary, 0.1)

  switch (token) {
    case 'primaryContainer':
      return { backgroundColor: primaryBg, color: theme.palette.primary.main }
    case 'secondaryContainer':
      return { backgroundColor: secondaryBg, color: theme.palette.secondary.main }
    case 'tertiaryContainer':
      return { backgroundColor: infoBg, color: theme.palette.info.main }
    case 'errorContainer':
      return { backgroundColor: errorBg, color: theme.palette.error.main }
    case 'surfaceContainerHigh':
      return { backgroundColor: surfaceHighBg, color: theme.palette.text.primary }
    default:
      return { backgroundColor: surfaceBg, color: theme.palette.text.primary }
  }
}

export interface ClockInstant {
  epochMs?: number | null | undefined
  epochSeconds?: number | null | undefined
  isoString?: string | null | undefined
  isoUtc?: string | null | undefined
  stationLocal?: string | null | undefined
}

export const resolveClockEpochMs = (
  value: ClockInstant | string | null | undefined,
  stationTz?: string | null,
): number | null => {
  if (value === null || value === undefined) {
    return null
  }

  if (typeof value === 'string') {
    const trimmed = value.trim()
    if (stationTz && stationTz.trim().length > 0) {
      const looksLocal = /^(\d{4})-(\d{2})-(\d{2})\s+\d{2}:\d{2}(?::\d{2})?$/.test(trimmed)
      if (looksLocal) {
        const localEpoch = stationLocalDateTimeToEpochMs(trimmed, stationTz)
        if (localEpoch !== null) {
          return localEpoch
        }
      }
    }
    const isoParsed = resolveEpochMs({ isoString: value })
    if (isoParsed !== null) {
      return isoParsed
    }
    if (stationTz && stationTz.trim().length > 0) {
      return stationLocalDateTimeToEpochMs(value, stationTz)
    }
    return null
  }

  const epoch = resolveEpochMs({
    epochMs: value.epochMs,
    epochSeconds: value.epochSeconds,
    isoString: value.isoString ?? value.isoUtc ?? null,
  })

  if (epoch !== null) {
    return epoch
  }

  if (value.stationLocal && stationTz && stationTz.trim().length > 0) {
    return stationLocalDateTimeToEpochMs(value.stationLocal, stationTz)
  }

  return null
}

const formatClockLocalTimeString = (raw: string): string => {
  const trimmed = raw.trim()
  if (!trimmed) {
    return '—'
  }
  const match = trimmed.match(/(\d{2}):(\d{2})(?::\d{2})?$/)
  if (match) {
    return `${match[1]}:${match[2]}`
  }
  return trimmed
}

export interface ClockTimeLabelOptions {
  withSeconds?: boolean
  twentyFourHour?: boolean
}

export const formatClockTimeLabel = (
  value: ClockInstant | string | null | undefined,
  stationTz?: string | null,
  options: ClockTimeLabelOptions = {},
): string => {
  const { withSeconds = false, twentyFourHour = false } = options
  const epochMs = resolveClockEpochMs(value, stationTz)
  if (epochMs !== null) {
    const timeZone = resolveStationTimeZone(stationTz)
    return formatStationTime(epochMs, timeZone, {
      hour: twentyFourHour ? '2-digit' : 'numeric',
      minute: '2-digit',
      hour12: !twentyFourHour,
      ...(withSeconds ? { second: '2-digit' } : {}),
    })
  }

  if (typeof value === 'string') {
    return formatClockLocalTimeString(value)
  }

  if (value?.stationLocal) {
    return formatClockLocalTimeString(value.stationLocal)
  }

  return '—'
}

export const formatClockTimeLabel24s = (epochMs: number, stationTz: string): string => {
  if (!Number.isFinite(epochMs)) {
    return '—'
  }

  const safeEpoch = Math.trunc(epochMs)
  const timeZone = resolveStationTimeZone(stationTz)

  return formatStationTime(safeEpoch, timeZone, {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  })
}

export const formatDriftWithSign = (seconds: number): string => {
  if (!Number.isFinite(seconds)) {
    return 'On time'
  }
  const rounded = Math.round(seconds)
  if (Math.abs(rounded) <= 59) {
    return 'On time'
  }
  const sign = rounded < 0 ? '−' : '+'
  const abs = Math.abs(rounded)
  const mm = Math.floor(abs / 60)
    .toString()
    .padStart(1, '0')
  const ss = (abs % 60)
    .toString()
    .padStart(2, '0')
  return `${sign}${mm}:${ss}`
}

export type AnchorSlackTier = 'ahead' | 'tight' | 'behindOrange' | 'behindRed' | 'unknown'

const SLACK_TIGHT_SECONDS = 60
const SLACK_BEHIND_RED_SECONDS = -60

export const slackTierForSeconds = (value: number | null | undefined): AnchorSlackTier => {
  if (!Number.isFinite(value ?? Number.NaN)) {
    return 'unknown'
  }
  const slack = Number(value)
  if (slack > SLACK_TIGHT_SECONDS) {
    return 'ahead'
  }
  if (slack >= 0) {
    return 'tight'
  }
  if (slack > SLACK_BEHIND_RED_SECONDS) {
    return 'behindOrange'
  }
  return 'behindRed'
}

export const driftColor = (seconds: number, theme: Theme): string => {
  if (!Number.isFinite(seconds) || Math.abs(seconds) <= 59) {
    return theme.palette.success.main
  }
  return seconds < 0 ? theme.palette.error.main : theme.palette.warning.main
}

export const urgencyColor = (secondsLeft: number, theme: Theme): string | undefined => {
  if (!Number.isFinite(secondsLeft)) {
    return undefined
  }
  if (secondsLeft <= 10) {
    return theme.palette.error.main
  }
  if (secondsLeft <= 20) {
    return theme.palette.warning.dark ?? theme.palette.warning.main
  }
  if (secondsLeft <= 30) {
    return theme.palette.warning.light ?? theme.palette.warning.main
  }
  return undefined
}

export const currentItemCardStyles = (
  theme: Theme,
  colorToken: string,
): { borderColor: string; backgroundColor: string; boxShadow: string } => {
  const colorStyles = resolveClockItemColorStyles(theme, colorToken)
  const borderColor = theme.palette.common.white
  const backgroundTint = alpha(
    borderColor,
    theme.palette.mode === 'dark' ? 0.04 : 0.03,
  )

  return {
    borderColor,
    backgroundColor: backgroundTint,
    boxShadow: `${alpha(borderColor, 0.5)} 0px 0px 12px, ${theme.shadows[24]}`,
  }
}

export const formatClockDateLabel = (
  value: ClockInstant | string | null | undefined,
  stationTz?: string | null,
  referenceEpochMs: number = Date.now(),
): string => {
  const epochMs = resolveClockEpochMs(value, stationTz)
  if (epochMs !== null) {
    const timeZone = resolveStationTimeZone(stationTz)
    const base = formatStationTime(epochMs, timeZone, {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
    })
    const year = Number(formatStationTime(epochMs, timeZone, { year: 'numeric' }))
    const currentYear = Number(formatStationTime(referenceEpochMs, timeZone, { year: 'numeric' }))
    if (!Number.isNaN(year) && year !== currentYear) {
      return `${base} ${year}`
    }
    return base
  }

  if (typeof value === 'string') {
    return value || '—'
  }

  return value?.stationLocal ?? '—'
}

export const formatClockDuration = (seconds: number | null | undefined): string => {
  if (!Number.isFinite(seconds) || seconds === null || seconds === undefined) {
    return '0:00'
  }
  const totalSeconds = Math.max(0, Math.round(Number(seconds)))
  const minutes = Math.floor(totalSeconds / 60)
  const remainingSeconds = totalSeconds % 60
  return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`
}

export const parseClockDurationInput = (value: string): number | null => {
  const trimmed = value.trim()
  if (!trimmed) {
    return null
  }

  const colonParts = trimmed.split(':')
  if (colonParts.length === 1) {
    const seconds = Number(trimmed)
    if (Number.isFinite(seconds) && seconds >= 0) {
      return Math.round(seconds)
    }
    return null
  }

  if (colonParts.length === 2) {
    const [minutesPart, secondsPart] = colonParts
    const minutes = Number(minutesPart)
    const seconds = Number(secondsPart)
    if (
      Number.isFinite(minutes) &&
      Number.isFinite(seconds) &&
      minutes >= 0 &&
      seconds >= 0 &&
      seconds < 60
    ) {
      return Math.round(minutes * 60 + seconds)
    }
    return null
  }

  if (colonParts.length === 3) {
    const [hoursPart, minutesPart, secondsPart] = colonParts
    const hours = Number(hoursPart)
    const minutes = Number(minutesPart)
    const seconds = Number(secondsPart)
    if (
      Number.isFinite(hours) &&
      Number.isFinite(minutes) &&
      Number.isFinite(seconds) &&
      hours >= 0 &&
      minutes >= 0 &&
      minutes < 60 &&
      seconds >= 0 &&
      seconds < 60
    ) {
      return Math.round(hours * 3600 + minutes * 60 + seconds)
    }
    return null
  }

  return null
}

const DAY_MS = 24 * 60 * 60 * 1000

export const formatClockRelativeDate = (
  value: ClockInstant | string | null,
  stationTz?: string | null,
  nowEpochMs: number = Date.now(),
): string => {
  if (value === null || value === undefined) {
    return 'Never'
  }

  const epochMs = resolveClockEpochMs(value, stationTz)
  if (epochMs === null) {
    if (typeof value === 'string') {
      return value
    }
    if (value.stationLocal) {
      return value.stationLocal
    }
    return 'Never'
  }

  const timeZone = resolveStationTimeZone(stationTz)
  const diff = nowEpochMs - epochMs
  if (diff < DAY_MS && diff > -DAY_MS) {
    return formatStationTime(epochMs, timeZone, { hour: 'numeric', minute: '2-digit' })
  }

  const includeYear =
    Number(formatStationTime(epochMs, timeZone, { year: 'numeric' })) !==
    Number(formatStationTime(nowEpochMs, timeZone, { year: 'numeric' }))

  return formatStationTime(epochMs, timeZone, {
    month: 'short',
    day: 'numeric',
    year: includeYear ? 'numeric' : undefined,
  })
}
